import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../../firebaseConfig';
import { doc, getDoc, setDoc, collection } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';

import { FaPlus, FaTrashAlt, FaImage, FaTimes, FaSpinner } from 'react-icons/fa';
import '../../styles/ScaleManager.css';

const ScaleManagerModal = ({ scaleId, adminEmpresaId, onClose, onScalesUpdate }) => {
    const [scaleName, setScaleName] = useState('');
    const [scaleLevels, setScaleLevels] = useState([{ value: 1, emoji: '😞', label: '', imageUrl: null }]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [pickerState, setPickerState] = useState({ show: false, index: null, position: {} });
    const [uploadingImageForLevel, setUploadingImageForLevel] = useState(null);
    
    const iconButtonRefs = useRef([]);
    const storage = getStorage();

    useEffect(() => {
        const fetchScaleData = async () => {
            if (scaleId) {
                setLoading(true);
                const scaleDocRef = doc(db, 'Empresas', adminEmpresaId, 'evaluationScales', scaleId);
                const docSnap = await getDoc(scaleDocRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setScaleName(data.name);
                    setScaleLevels(data.levels || [{ value: 1, emoji: '😞', label: '', imageUrl: null }]);
                } else { setError('Escala não encontrada.'); }
                setLoading(false);
            }
        };
        fetchScaleData();
    }, [scaleId, adminEmpresaId]);

    const handleLevelChange = (index, field, value) => {
        setScaleLevels(currentLevels =>
            currentLevels.map((level, i) => i === index ? { ...level, [field]: value } : level)
        );
    };
    
    const handleEmojiPickerToggle = (index) => {
        if (pickerState.show && pickerState.index === index) {
            setPickerState({ show: false, index: null, position: {} });
        } else {
            const buttonRect = iconButtonRefs.current[index].getBoundingClientRect();
            const spaceBelow = window.innerHeight - buttonRect.bottom;
            const pickerHeight = 435; // Altura aproximada do picker
            
            const position = {
                top: spaceBelow < pickerHeight ? buttonRect.top - pickerHeight - 10 : buttonRect.bottom + 10,
                left: buttonRect.left
            };

            setPickerState({ show: true, index: index, position });
        }
    };

    const onEmojiSelect = (emoji) => {
        if (pickerState.index !== null) {
            handleLevelChange(pickerState.index, 'emoji', emoji.native);
            handleLevelChange(pickerState.index, 'imageUrl', null);
            setPickerState({ show: false, index: null, position: {} });
        }
    };

    const handleImageUpload = async (index, file) => {
        if (!file || !adminEmpresaId) return;
        setUploadingImageForLevel(index);
        
        const idToSave = scaleId || doc(collection(db, 'tmp')).id;
        const fileRef = ref(storage, `Empresas/${adminEmpresaId}/scaleImages/${idToSave}/${Date.now()}_${file.name}`);
        
        try {
            const snapshot = await uploadBytes(fileRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            handleLevelChange(index, 'imageUrl', downloadURL);
            handleLevelChange(index, 'emoji', '');
        } catch (err) {
            console.error("Erro no upload da imagem:", err);
            setError('Falha ao enviar a imagem.');
        } finally {
            setUploadingImageForLevel(null);
        }
    };

    const removeImage = (index) => {
        handleLevelChange(index, 'imageUrl', null);
        handleLevelChange(index, 'emoji', '😀');
    };

    const addLevel = () => {
        const lastValue = scaleLevels.length > 0 ? scaleLevels[scaleLevels.length - 1].value : 0;
        setScaleLevels([...scaleLevels, { value: lastValue + 1, emoji: '😀', label: '', imageUrl: null }]);
    };

    const removeLevel = (index) => {
        if (scaleLevels.length <= 1) { setError('A escala deve ter pelo menos um nível.'); return; }
        setScaleLevels(scaleLevels.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        setError('');
        if (!scaleName.trim()) { setError('O nome da escala é obrigatório.'); return; }
        if (scaleLevels.some(level => !level.label.trim())) { setError('Todos os níveis devem ter um rótulo (descrição).'); return; }

        setLoading(true);
        const idToSave = scaleId || doc(collection(db, 'tmp')).id;
        const scaleDocRef = doc(db, 'Empresas', adminEmpresaId, 'evaluationScales', idToSave);
        
        const min = Math.min(...scaleLevels.map(l => l.value));
        const max = Math.max(...scaleLevels.map(l => l.value));

        try {
            await setDoc(scaleDocRef, { name: scaleName, levels: scaleLevels, min, max });
            onScalesUpdate();
            onClose();
        } catch (err) {
            console.error(err); setError('Falha ao salvar a escala.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {pickerState.show && createPortal(
                <div style={{ position: 'fixed', top: `${pickerState.position.top}px`, left: `${pickerState.position.left}px`, zIndex: 1100 }}>
                    <Picker data={data} onEmojiSelect={onEmojiSelect} onClickOutside={() => setPickerState({ show: false, index: null, position: {} })} />
                </div>,
                document.body
            )}
            <div className="sm-modal-backdrop" onMouseDown={() => setPickerState({ show: false, index: null, position: {} })}>
                <div className="sm-modal-content" onMouseDown={e => e.stopPropagation()}>
                    <div className="sm-modal-header">
                        <h3>{scaleId ? 'Editar Escala' : 'Criar Nova Escala'}</h3>
                        <button onClick={onClose} className="sm-modal-close-button">&times;</button>
                    </div>

                    <div className="sm-modal-body">
                        {error && <div className="sm-modal-error-bar">{error}</div>}

                        <div className="sm-modal-section">
                            <h4>1. Detalhes da Escala</h4>
                            <div className="sm-modal-form-group">
                                <label htmlFor="scaleName">Nome da Escala</label>
                                <input id="scaleName" type="text" className="sm-modal-input" value={scaleName} onChange={(e) => setScaleName(e.target.value)} placeholder="Ex: Nível de Engajamento" />
                            </div>
                        </div>
                        
                        <div className="sm-modal-section">
                            <h4>2. Níveis da Escala</h4>
                            <div className="sm-modal-levels-container">
                                <div className="sm-modal-grid-header">
                                    <span className="sm-header-value">Valor</span>
                                    <span className="sm-header-icon">Ícone</span>
                                    <span className="sm-header-label">Rótulo / Descrição</span>
                                    <span className="sm-header-action">Ação</span>
                                </div>

                                {scaleLevels.map((level, index) => (
                                    <div className="sm-modal-level-row" key={index}>
                                        <div className="sm-modal-grid-cell sm-cell-value">
                                            <label className="sm-mobile-label">Valor</label>
                                            <input type="number" className="sm-modal-input" value={level.value} onChange={(e) => handleLevelChange(index, 'value', Number(e.target.value))} />
                                        </div>
                                        
                                        <div className="sm-modal-grid-cell sm-cell-icon">
                                            <label className="sm-mobile-label">Ícone</label>
                                            <div className="sm-modal-icon-container">
                                                <div
                                                    className="sm-modal-icon-display"
                                                    ref={el => iconButtonRefs.current[index] = el}
                                                    onClick={() => !level.imageUrl && handleEmojiPickerToggle(index)}
                                                >
                                                    {uploadingImageForLevel === index ? <FaSpinner className="sm-modal-spinner" /> :
                                                     level.imageUrl ? <img src={level.imageUrl} alt="preview" className="sm-modal-image-preview" /> :
                                                     level.emoji}
                                                </div>
                                                <div className="sm-modal-icon-actions">
                                                    <label htmlFor={`image-upload-${index}`} className="sm-modal-icon-btn" title="Enviar Imagem"><FaImage /></label>
                                                    <input id={`image-upload-${index}`} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleImageUpload(index, e.target.files[0])} />
                                                    {level.imageUrl && <button onClick={() => removeImage(index)} className="sm-modal-icon-btn" title="Remover Imagem"><FaTimes /></button>}
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="sm-modal-grid-cell sm-cell-label">
                                            <label className="sm-mobile-label">Rótulo / Descrição</label>
                                            <input type="text" className="sm-modal-input" value={level.label} onChange={(e) => handleLevelChange(index, 'label', e.target.value)} placeholder="Ex: Excepcional" />
                                        </div>

                                        <div className="sm-modal-grid-cell sm-cell-action">
                                            <button onClick={() => removeLevel(index)} className="sm-modal-icon-btn sm-modal-delete-level-btn" title="Remover Nível"><FaTrashAlt /></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button onClick={addLevel} className="sm-modal-btn-add-level"><FaPlus /> Adicionar Nível</button>
                        </div>
                    </div>

                    <div className="sm-modal-footer">
                        <button onClick={onClose} className="btn btn-secondary">Cancelar</button>
                        <button onClick={handleSave} className="btn btn-primary" disabled={loading || uploadingImageForLevel !== null}>
                            {loading ? 'Salvando...' : (uploadingImageForLevel !== null ? 'Enviando imagem...' : 'Salvar Escala')}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ScaleManagerModal;